/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { Trans, useTranslation } from 'react-i18next';

/**
 * CHECKOUT-14 — Terms & Conditions acceptance version.
 *
 * Stamped alongside the acceptance so we can prove WHICH version of the terms a
 * shopper agreed to at order time (legacy SiteGenesis recorded the same).
 *
 * NOTE(confirm-before-launch): hard-coded placeholder. The real source of truth
 * for the live T&C version (a site preference or the content-asset revision)
 * is an open question on the ticket — wire it in before go-live.
 */
export const TERMS_AND_CONDITIONS_VERSION = '2026-01';

/**
 * NOTE(confirm-before-launch): placeholder URL for the Terms & Conditions
 * content. Legacy renders a content-asset link; the production target is an
 * open question on the ticket. Opens in a new tab so the shopper does not lose
 * checkout progress.
 */
export const TERMS_OF_USE_HREF = '/terms-and-conditions';
export const PRIVACY_POLICY_HREF = '/privacy-policy';

export interface TermsAndConditionsProps {
    /** Target for the Terms of Use link. */
    termsHref?: string;
    /** Target for the Privacy Policy link. */
    privacyHref?: string;
    /** Optional class name for the outer container. */
    className?: string;
}

/**
 * Terms & Conditions acceptance gate for the order-review step.
 *
 * Figma parity: legal acknowledgment is implied by placement (no explicit
 * checkbox gate in the review section).
 */
export default function TermsAndConditions({
    termsHref = TERMS_OF_USE_HREF,
    privacyHref = PRIVACY_POLICY_HREF,
    className,
}: TermsAndConditionsProps) {
    useTranslation('checkout');

    return (
        <div className={className} data-testid="terms-and-conditions">
            <p className="text-sm leading-5 text-foreground" data-testid="terms-and-conditions-label">
                <Trans
                    i18nKey="checkout:reviewOrder.terms.impliedConsent"
                    components={{
                        termsLink: (
                            <a
                                href={termsHref}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="font-semibold underline underline-offset-2"
                                data-testid="terms-of-use-link"
                            />
                        ),
                        privacyLink: (
                            <a
                                href={privacyHref}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="font-semibold underline underline-offset-2"
                                data-testid="privacy-policy-link"
                            />
                        ),
                    }}
                />
            </p>
        </div>
    );
}
